import { useState, useEffect, useCallback, useRef } from "react";
import API_ENDPOINTS from "../utilities/APIRoutes";

const useChatSessions = (AuthToken, UserToken, showSnackbar) => {
  const [chatSession, setChatSessions] = useState([]);
  const [renameModalOpen, setRenameModalOpen] = useState(false);
  const [newChatName, setNewChatName] = useState("");
  const [selectedChatForMenu, setSelectedChatForMenu] = useState(null);
  const [anchorEl, setAnchorEl] = useState(null);
  const inFlightFetch = useRef(null);

  const fetchChatSessions = useCallback(async () => {
    if (!AuthToken || !UserToken) return;
    // Dedupe concurrent fetches: if one is in flight, callers share it.
    // Prevents a stale GET (started earlier) from overwriting a fresher one.
    if (inFlightFetch.current) return inFlightFetch.current;

    const request = (async () => {
      try {
        const response = await fetch(API_ENDPOINTS.ListChats, {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            "User-Token": UserToken,
            Authorization: AuthToken,
          },
        });
        const data = await response.json();

        if (data.message && Array.isArray(data.message)) {
          const sortedChatSessions = data.message.sort(
            (a, b) => new Date(b.createdAt) - new Date(a.createdAt)
          );
          setChatSessions(sortedChatSessions);
        } else {
          console.warn("No chat sessions found or invalid response format.");
          setChatSessions([]);
        }
      } catch (error) {
        showSnackbar("Error fetching chat sessions", "error");
        console.error("Error fetching chat sessions:", error);
      } finally {
        inFlightFetch.current = null;
      }
    })();

    inFlightFetch.current = request;
    return request;
  }, [AuthToken, UserToken, showSnackbar]);

  useEffect(() => {
    fetchChatSessions();
  }, [fetchChatSessions]);

  const handleMenuOpen = (event, chat) => {
    event.stopPropagation();
    setAnchorEl(event.currentTarget);
    setSelectedChatForMenu(chat);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setSelectedChatForMenu(null);
  };

  const openRenameModal = () => {
    setNewChatName(selectedChatForMenu?.chatName || "");
    setRenameModalOpen(true);
    setAnchorEl(null);
  };

  const closeRenameModal = () => {
    setRenameModalOpen(false);
    setSelectedChatForMenu(null);
  };

  const handleRenameChat = async () => {
    if (!selectedChatForMenu) return;
    try {
      const response = await fetch(API_ENDPOINTS.RenameChat, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: AuthToken,
          "User-Token": UserToken,
        },
        body: JSON.stringify({
          chatName: newChatName,
          chatID: selectedChatForMenu.id,
        }),
      });

      if (!response.ok) throw new Error("Rename failed");

      const updatedSessions = chatSession.map((chat) =>
        chat.id === selectedChatForMenu.id
          ? { ...chat, chatName: newChatName }
          : chat
      );
      setChatSessions(updatedSessions);
      showSnackbar("Chat renamed successfully!", "success");
      closeRenameModal();
    } catch (error) {
      showSnackbar("Failed to rename chat.", "error");
      closeRenameModal();
    }
  };

  return {
    chatSession,
    setChatSessions,
    renameModalOpen,
    setRenameModalOpen,
    newChatName,
    setNewChatName,
    handleRenameChat,
    anchorEl,
    handleMenuOpen,
    handleMenuClose,
    openRenameModal,
    closeRenameModal,
    selectedChatForMenu,
    setSelectedChatForMenu,
    fetchChatSessions,
  };
};

export default useChatSessions; 